export interface GitHubRelease {
  id: number;
  tag_name: string;
  name: string;
  body: string;
  published_at: string;
  assets: GitHubAsset[];
  html_url: string;
}

export interface GitHubAsset {
  id: number;
  name: string;
  browser_download_url: string;
  size: number;
  download_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Fetches the latest release from a GitHub repository
 * @param owner The repository owner
 * @param repo The repository name
 * @returns The latest release information
 */
export const fetchLatestRelease = async (owner: string, repo: string): Promise<GitHubRelease | null> => {
  try {
    const response = await fetch(`https://api.github.com/repos/${owner}/${repo}/releases/latest`);

    if (!response.ok) {
      throw new Error(`GitHub API error: ${response.status}`);
    }

    const release: GitHubRelease = await response.json();
    return release;
  } catch (error) {
    console.error('Error fetching latest release:', error);
    return null;
  }
};

/**
 * Fetches all releases from a GitHub repository (with pagination)
 * @param owner The repository owner
 * @param repo The repository name
 * @param perPage Number of releases per page (default: 10)
 * @returns Array of releases
 */
export const fetchReleases = async (owner: string, repo: string, perPage: number = 10): Promise<GitHubRelease[]> => {
  try {
    const response = await fetch(`https://api.github.com/repos/${owner}/${repo}/releases?per_page=${perPage}`);

    if (!response.ok) {
      throw new Error(`GitHub API error: ${response.status}`);
    }

    const releases: GitHubRelease[] = await response.json();
    return releases;
  } catch (error) {
    console.error('Error fetching releases:', error);
    return [];
  }
};

/**
 * Finds the Windows installer asset from a release
 * @param release The GitHub release
 * @returns The Windows installer asset or null
 */
export const findWindowsInstaller = (release: GitHubRelease): GitHubAsset | null => {
  return release.assets.find(asset =>
    asset.name.toLowerCase().includes('.exe') ||
    asset.name.toLowerCase().includes('setup') ||
    asset.name.toLowerCase().includes('installer')
  ) || null;
};

/**
 * Formats release notes by converting markdown links and basic formatting
 * @param body The release body markdown
 * @returns Formatted HTML string
 */
export const formatReleaseNotes = (body: string): string => {
  if (!body) return '';

  // Convert basic markdown to HTML
  let formatted = body
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
    .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
    .replace(/`(.*?)`/g, '<code>$1</code>') // Code
    .replace(/\n\n/g, '</p><p>') // Paragraphs
    .replace(/\n/g, '<br>') // Line breaks
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>'); // Links

  return `<p>${formatted}</p>`;
};